import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CalendarCheck, Inbox, Star } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { IMAGES, CONTACT } from "../lib/site";

const BOOK_LINK = "/contact";

const problems = [
  "Inquiries come in when no one is available to answer.",
  "Leads wait too long for a reply and go elsewhere.",
  "Scheduling, reminders and follow-up depend on someone remembering.",
  "The same information gets handled by hand, again and again.",
];

const systems = [
  {
    icon: Inbox,
    title: "Front Desk System",
    text: "Answers calls and messages 24/7, captures every inquiry and hands off to a person when it matters.",
  },
  {
    icon: CalendarCheck,
    title: "Booking & Scheduling System",
    text: "Lets customers book on their own, with confirmations and reminders sent automatically.",
  },
  {
    icon: Star,
    title: "Follow-Up & Reviews System",
    text: "Stays in touch with past customers, asks for reviews and brings people back.",
  },
];

const steps = [
  {
    n: "1",
    title: "Consult",
    text: "A free 60-minute consultation. We find where you're losing customers and time, and map your next steps.",
  },
  {
    n: "2",
    title: "Map",
    text: "We capture how your business operates: your hours, services, common questions, your tone and the tools you already use.",
  },
  {
    n: "3",
    title: "Build",
    text: "We turn it into clear rules: what gets answered, what gets scheduled, and when a person steps in.",
  },
  {
    n: "4",
    title: "Launch & Manage",
    text: "We test it, walk you through it and go live. Then we keep it running and improve it every month.",
  },
];

const faqs = [
  { q: "Do I need to learn new software?", a: "No. We build it, run it and maintain it." },
  { q: "Will it replace my team?", a: "No. It handles the repeat work so your people can focus on customers." },
  { q: "What if something breaks?", a: "We monitor it and fix issues, usually before you notice." },
  { q: "What does it cost?", a: "It depends on your business. We'll walk through it after your free consultation." },
];

const Landing = () => (
  <>
    {/* 1. Hero */}
    <section className="relative overflow-hidden bg-skybrand-50">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-skybrand-50 to-skybrand-100/60" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-navy leading-[1.08]"
            >
              <MaskedLines lines={["We build the systems", "your business runs on."]} />
            </h1>
            <motion.p
              data-testid="landing-subtext"
              className="mt-6 max-w-xl font-sans text-lg sm:text-xl leading-relaxed text-slate-600"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              Every business loses time and customers in the gaps: the missed call, the slow reply, the
              follow-up that never happens. We take how your business really runs and turn it into one system
              your whole team can use.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={BOOK_LINK}
                data-testid="hero-book-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-sans text-base font-semibold text-navy-dark transition-all duration-300 hover:bg-gold-hover hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(201,162,39,0.6)]"
              >
                Book Your Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={CONTACT.phoneHref}
                data-testid="hero-call-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy px-7 py-4 font-sans text-base font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-4 h-4" /> Call Our Front Desk System Now
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="absolute -inset-3 rounded-[2rem] border border-gold/50" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1788995860031-1e22a61c9a46?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Two small business partners standing inside their warm, welcoming shop"
                className="relative rounded-[1.75rem] border-4 border-white shadow-2xl h-[340px] sm:h-[440px] w-full object-cover"
                data-testid="landing-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>

    {/* 2. The problem */}
    <section className="bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal>
          <Eyebrow>The problem</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-navy leading-[1.18]">
            Your business has outgrown the way it runs.
          </h2>
        </Reveal>
        <ul className="mt-10 space-y-5">
          {problems.map((p, i) => (
            <Reveal key={p} delay={0.08 + i * 0.07}>
              <li className="flex items-start gap-4">
                <span className="mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                <p className="font-sans text-base sm:text-lg leading-relaxed text-slate-700">{p}</p>
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.35}>
          <p className="mt-10 font-serif italic text-xl sm:text-2xl text-navy/85 leading-snug">
            The cost doesn't show up on an invoice. It shows up as lost customers and lost hours.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 3. Who we serve */}
    <section className="relative overflow-hidden bg-navy">
      <div className="absolute inset-x-0 top-0 h-px gold-rule" aria-hidden="true" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 text-center">
        <Reveal>
          <p className="font-sans text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-gold">Who we serve</p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white leading-[1.18]">
            Built for businesses ready to run better.
          </h2>
          <p className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl mx-auto">
            Whatever you sell and wherever you operate, if your team repeats the same tasks every day, we build
            the system that handles them.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 4. What we build */}
    <section className="bg-skybrand-50 border-y border-skybrand-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal className="max-w-3xl">
          <Eyebrow>What we build</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-navy leading-[1.18]">
            If it's done over and over in your business, we can build a system for it.
          </h2>
          <p className="mt-5 font-sans text-base sm:text-lg font-medium text-slate-600">
            Where most businesses start:
          </p>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {systems.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <article
                data-testid={`system-card-${i}`}
                className="group h-full rounded-2xl bg-white border border-slate-200 p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-gold-border"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-skybrand-100 text-navy transition-colors duration-300 group-hover:bg-gold-wash group-hover:text-gold-deep">
                  <s.icon className="w-6 h-6" />
                </span>
                <h3 className="mt-6 font-serif text-xl sm:text-2xl font-semibold text-navy">{s.title}</h3>
                <p className="mt-3 font-sans text-base leading-relaxed text-slate-600">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <p className="mt-10 font-serif italic text-lg sm:text-xl text-navy/85">
            Something else slowing you down? We'll find it in your free consultation.
          </p>
          <p className="mt-4 font-sans text-sm text-slate-500">
            Every system is built around your business, then hosted, monitored and maintained by us.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 5. How it works */}
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal className="max-w-3xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-navy leading-[1.18]">
            Four steps from first call to running system
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <article className="h-full rounded-2xl border border-slate-200 bg-skybrand-50/60 p-7" data-testid={`step-card-${s.n}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold bg-white font-serif text-xl font-semibold text-gold-deep">
                  {s.n}
                </span>
                <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 font-sans text-sm sm:text-base leading-relaxed text-slate-600">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 6. Our promise */}
    <section className="relative overflow-hidden bg-navy">
      <div className="absolute inset-x-0 top-0 h-px gold-rule" aria-hidden="true" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 text-center">
        <Reveal>
          <p className="font-sans text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-gold">Our promise</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.12]">
            Most systems go live within 21 days.
          </h2>
          <p className="mt-5 font-sans text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl mx-auto">
            From your first consultation to a working system, built around how your business already runs.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 7. See it for yourself */}
    <section className="bg-skybrand-50 border-b border-skybrand-border">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <Reveal>
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-gold-border text-gold-deep">
            <Phone className="w-7 h-7" />
          </span>
          <h2 className="mt-7 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-navy leading-[1.18]">
            Call our front desk.
          </h2>
          <p className="mt-5 font-sans text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl mx-auto">
            The system answering our phone is the same kind we build for you. Call it, ask a question, book a time.
          </p>
          <a
            href={CONTACT.phoneHref}
            data-testid="call-front-desk-button"
            className="mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-sans text-base font-semibold text-white transition-all duration-300 hover:bg-navy-light hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4" /> Call {CONTACT.phone}
          </a>
        </Reveal>
      </div>
    </section>

    {/* 8. Questions */}
    <section className="bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal className="text-center">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-navy leading-[1.18]">
            Asked before, answered plainly
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mt-10">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-accordion-item-${i}`}
                className="rounded-xl border border-slate-200 bg-white px-6 data-[state=open]:border-gold-border"
              >
                <AccordionTrigger className="font-serif text-lg sm:text-xl font-semibold text-navy text-left hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-sans text-base leading-relaxed text-slate-600 pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>

    {/* 9. Final CTA */}
    <section className="relative overflow-hidden bg-navy">
      <div className="absolute inset-x-0 top-0 h-px gold-rule" aria-hidden="true" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.12]">
            Find out what your current setup is costing you.
          </h2>
          <div className="mt-10">
            <Link
              to={BOOK_LINK}
              data-testid="final-cta-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-sans text-base font-semibold text-navy-dark transition-all duration-300 hover:bg-gold-hover hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(201,162,39,0.6)]"
            >
              Book Your Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Landing;
